import {
    createUserWithEmailAndPassword,
    getRedirectResult,
    GoogleAuthProvider,
    onIdTokenChanged,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    signOut,
    updateProfile,
    type User as FirebaseUser,
} from "firebase/auth";

import { auth } from "@/lib/firebase/client.ts";
import type { Role, User } from "@/models/user";
import type { DataSubmissionRepository } from "@/repositories/data-submission-repository.ts";
import { UserRepository } from "@/repositories/user-repository";

interface AuthError {
    code?: string;
    message?: string;
}

function isAuthError(e: unknown): e is AuthError {
    return typeof e === "object" && e !== null && "code" in e;
}

export default class AuthService {
    private static instance: AuthService;

    private readonly userRepo: UserRepository;
    private readonly dataSubmissionRepo: DataSubmissionRepository;
    private unsubscribe?: () => void;
    public currentUser: User | null = null;
    public currentFirebaseUser: FirebaseUser | null = null;
    public idToken: string | undefined;

    public authed = false;
    public initialized = false;

    // Centralized state management
    private subscribers = new Set<(user: User | null) => void>();

    public static getInstance(
        userRepo: UserRepository | undefined = undefined,
        dataSubmissionRepo: DataSubmissionRepository | undefined = undefined,
    ): AuthService {
        if (!AuthService.instance && userRepo && dataSubmissionRepo) {
            AuthService.instance = new AuthService(userRepo, dataSubmissionRepo);
        }
        if (this.instance.userRepo) {
            return AuthService.instance;
        }
        throw new Error("AuthService must be initialized with a userRepo before use.");
    }

    constructor(userRepo: UserRepository, dataSubmissionRepo: DataSubmissionRepository) {
        this.userRepo = userRepo;
        this.dataSubmissionRepo = dataSubmissionRepo;
        this.init();
    }

    init() {
        if (this.unsubscribe) return; // idempotent
        this.unsubscribe = onIdTokenChanged(auth, async (fbUser) => {
            this.currentFirebaseUser = fbUser;
            this.idToken = await fbUser?.getIdToken();

            this.mapFirebaseUser(fbUser).then(async (mapped) => {
                this.currentUser = mapped;
                this.initialized = true;
                this.authed = !!mapped;
                this.notifySubscribers();

                // Ensure a user document exists/updated when signed in
                if (mapped) {
                    try {
                        await this.userRepo.upsert(mapped);
                    } catch (e) {
                        // Optional: log/report error; keep auth state functional
                        console.error("Failed to upsert user profile:", e);
                    }
                }
            });
        });

        return this;
    }

    async handleRedirectResultOnce(): Promise<void> {
        try {
            console.log("[Auth] Checking redirect result...");
            const res = await getRedirectResult(auth);
            if (res?.user) {
                const mapped = (await this.mapFirebaseUser(res.user))!;
                this.currentUser = mapped;
                this.authed = !!mapped;
                try {
                    await this.userRepo.upsert(mapped);
                } catch (e) {
                    console.error("[Auth] Upsert after redirect failed:", e);
                }
                console.log("[Auth] Redirect handled. User:", mapped);
            } else {
                console.log("[Auth] No redirect result found.");
            }
        } catch (e) {
            console.error("[Auth] getRedirectResult failed:", e);
        }
    }

    async signInWithEmailAndPassword(email: string, password: string): Promise<User> {
        try {
            const cred = await signInWithEmailAndPassword(auth, email, password);
            const mapped = (await this.mapFirebaseUser(cred.user))!;
            this.currentUser = mapped;
            this.authed = !!mapped;

            await this.userRepo.upsert(mapped);
            return mapped;
        } catch (err: unknown) {
            if (!isAuthError(err)) {
                throw err;
            }
            const reason = err as AuthError;

            console.error("Sign-in failed:", reason);

            console.log(reason.code);

            switch (reason.code) {
                case "auth/user-not-found":
                    throw { code: reason.code, message: "Az email cím nincs regisztrálva." };
                case "auth/invalid-credential":
                    throw { code: reason.code, message: "Érvénytelen jelszó / email páros" };
                case "auth/user-disabled":
                    throw { code: reason.code, message: "A felhasználó le van tiltva." };
                case "auth/network-request-failed":
                    throw { code: reason.code, message: "Hálózati hiba. Próbálja újra." };
                default:
                    throw { code: "unknown", message: "Hiba történt. Próbálja újra." };
            }
        }
    }

    async sendPasswordResetEmail(email: string): Promise<void> {
        await sendPasswordResetEmail(auth, email);
    }

    async signUpWithEmailAndPassword(email: string, password: string, user: Omit<User, "uid">): Promise<User> {
        try {
            const cred = await createUserWithEmailAndPassword(auth, email, password);

            await updateProfile(cred.user, { displayName: user.displayName });

            this.authed = !!user;

            await this.userRepo.add({ ...user, uid: cred.user.uid });
            this.currentUser = await this.mapFirebaseUser(cred.user)!;

            return this.currentUser!;
        } catch (err: unknown) {
            if (!isAuthError(err)) {
                throw err;
            }
            const reason = err as AuthError;

            console.error("Sign-up failed:", reason);

            switch (reason.code) {
                case "auth/email-already-in-use":
                    throw { code: reason.code, message: "Ez az email már foglalt." };
                case "auth/invalid-email":
                    throw { code: reason.code, message: "Érvénytelen email cím." };
                case "auth/weak-password":
                    throw { code: reason.code, message: "A jelszó túl gyenge." };
                default:
                    throw { code: "unknown", message: "Hiba történt. Próbálja újra." };
            }
        }
    }

    async signInWithGoogle(): Promise<User> {
        let mapped: User;

        const provider = new GoogleAuthProvider();
        if (window.location.hostname === "localhost") {
            const cred = await signInWithPopup(auth, provider);

            mapped = (await this.mapFirebaseUser(cred.user))!;
        } else {
            await signInWithRedirect(auth, provider);
            // This will trigger a full page redirect away from your app
            return new Promise<User>(() => {}); // satisfy return type; resolves on startup
        }

        this.currentUser = mapped;
        this.authed = !!mapped;
        // Best-effort upsert
        try {
            await this.userRepo.upsert(mapped);
        } catch (e) {
            console.error("Failed to upsert user after google sign-in:", e);
        }
        return mapped;
    }

    async signOut(): Promise<void> {
        await signOut(auth);
        this.currentUser = null;
        this.authed = false;
    }

    private notifySubscribers() {
        this.subscribers.forEach((cb) => cb(this.currentUser));
    }

    subscribe(callback: (user: User | null) => void): () => void {
        this.subscribers.add(callback);
        callback(this.currentUser); // emit the current state immediately

        return () => this.subscribers.delete(callback);
    }

    dispose() {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        this.authed = false;
    }

    async updateProfile(user: FirebaseUser, { displayName }: { displayName?: string }): Promise<void> {
        await updateProfile(user, { displayName: displayName });
        const mapped = (await this.mapFirebaseUser(user))!;
        try {
            await this.userRepo.upsert(mapped);
        } catch (e) {
            console.error("Failed to upsert user profile:", e);
        }
    }

    async getUserRole(user: FirebaseUser | null): Promise<Role> {
        if (!user) return "unassigned";
        const token = await user.getIdTokenResult();

        const roleMap: Record<string, string> = {
            admin: "admin",
            teacher: "teacher",
            model: "model",
        };

        const role = Object.keys(roleMap).find((key) => token.claims[key]);
        return role ? (roleMap[role] as Role) : "unassigned";
    }

    private async mapFirebaseUser(fbUser: FirebaseUser | null): Promise<User | null> {
        if (!fbUser) return null;
        const dataSubmission = await this.dataSubmissionRepo.getByData("userId", fbUser.uid);

        return {
            uid: fbUser.uid,
            displayName: fbUser.displayName ?? "", //(await this.userRepo.getById(fbUser.uid))?.displayName ?? "",
            email: fbUser.email ?? null,
            phone: fbUser.phoneNumber ?? null,
            submittedData: dataSubmission.length != 0,
            role: await this.getUserRole(fbUser),
        };
    }
}
